import { motion } from "framer-motion";
import { Apple, ArrowUpRight, Cpu, ExternalLink, Globe, Monitor, Smartphone } from "lucide-react";
import { Link } from "react-router-dom";
import { TiltCard } from "./TiltCard";

const PLATFORM_ICONS = {
  "Google Play": Smartphone,
  "App Store": Apple,
  "Desktop": Monitor,
  "Web": Globe,
};

const PlatformBadge = ({ label }) => {
  const Icon = PLATFORM_ICONS[label] || Globe;
  return (
    <span className="inline-flex items-center gap-1.5 rounded-md border border-gray-200 bg-white px-2 py-1 font-mono text-[10px] font-medium uppercase tracking-wide text-gray-600">
      <Icon size={11} />
      {label}
    </span>
  );
};

export const ProjectCard = ({ project, setHovered }) => {
  const isLink = Boolean(project.link);

  const openProject = () => {
    if (isLink) window.open(project.link, "_blank", "noopener,noreferrer");
  };

  const handleKey = (e) => {
    if (isLink && (e.key === "Enter" || e.key === " ")) {
      e.preventDefault();
      openProject();
    }
  };

  return (
    <TiltCard className="h-full transition-transform duration-300 hover:scale-[1.02]">
      <div
        className={`h-full group relative bg-gradient-to-br ${project.color} border border-gray-200 rounded-2xl p-4 md:p-5 flex flex-col justify-between transition-all duration-300 overflow-hidden hover:shadow-xl ${isLink ? "hover:border-gray-400 cursor-pointer" : "hover:border-gray-300"} shadow-sm`}
        onMouseEnter={() => setHovered(true)}
        onMouseLeave={() => setHovered(false)}
        onClick={openProject}
        onKeyDown={handleKey}
        role={isLink ? "button" : undefined}
        tabIndex={isLink ? 0 : undefined}
        aria-label={isLink ? `Open project: ${project.name}` : undefined}
      >
        {/* Subtle blueprint grid overlay */}
        <div
          className="absolute inset-0 opacity-[0.05] pointer-events-none"
          style={{
            backgroundImage:
              "linear-gradient(to right, #111 1px, transparent 1px), linear-gradient(to bottom, #111 1px, transparent 1px)",
            backgroundSize: "28px 28px",
          }}
        />

        {/* Ghost index number */}
        <span className="absolute -bottom-4 -right-2 font-ndot text-6xl leading-none text-ink/[0.04] select-none pointer-events-none">
          {String(project.id).padStart(2, "0")}
        </span>

        {/* Decorative CPU */}
        <motion.div
          className="absolute top-0 right-0 p-3 opacity-10 group-hover:opacity-25 transition-opacity"
          whileHover={{ rotate: 15, scale: 1.2 }}
        >
          <Cpu size={88} />
        </motion.div>

        <div className="flex items-start justify-between mb-4">
          <span className="inline-flex items-center gap-2 px-3 py-1 bg-ink text-white rounded-full text-[11px] font-bold uppercase tracking-wider z-10">
            <span className="relative flex h-1.5 w-1.5">
              <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-nothing-red opacity-75"></span>
              <span className="relative inline-flex rounded-full h-1.5 w-1.5 bg-nothing-red"></span>
            </span>
            {project.category}
          </span>

          {isLink ? (
            <span className="z-10 text-gray-400 group-hover:text-nothing-red transition-colors p-1 -mr-1" aria-hidden="true">
              <ExternalLink size={18} className="transition-transform group-hover:scale-110" />
            </span>
          ) : (
            <ArrowUpRight className="text-gray-400 z-10" />
          )}
        </div>

        {project.image && (
          <div className={`relative z-10 mb-3 overflow-hidden rounded-xl border border-gray-200 bg-white shrink-0 ${project.imageClassName || "h-8 w-8"}`}>
            <img
              src={project.image}
              alt={`${project.name} preview`}
              className="h-full w-full object-contain p-1.5"
              loading="lazy"
            />
          </div>
        )}

        <div className="relative z-10">
          <h3 className="text-2xl font-ndot mb-2 flex flex-wrap items-center gap-2.5">
            {project.name}
            {project.stats && (
              <span className="text-[11px] font-mono text-nothing-red bg-white border border-gray-200 rounded-full px-2.5 py-1">
                {project.stats}
              </span>
            )}
          </h3>
          <p className="text-sm text-gray-600 mb-3 max-w-md leading-relaxed">{project.desc}</p>

          {project.platforms && (
            <div className="flex flex-wrap gap-2 mb-3">
              {project.platforms.map((p) => (
                <PlatformBadge key={p} label={p} />
              ))}
            </div>
          )}

          <div className="space-y-1.5 mb-4">
            {project.features.map((feat, i) => (
              <div key={i} className="flex items-center gap-2 text-[11px] font-mono text-gray-500">
                <div className="w-1 h-1 bg-nothing-red rounded-full" />
                [{feat}]
              </div>
            ))}
          </div>
        </div>

        <div className="flex items-end justify-between relative z-10 pt-4 border-t border-gray-200/70 gap-4">
          <div className="flex flex-wrap gap-1.5">
            {project.tech.map(t => (
              <span key={t} className="px-2 py-1 bg-white/80 backdrop-blur-sm border border-gray-200 text-[10px] font-medium text-gray-600 rounded-md">
                {t}
              </span>
            ))}
          </div>

          <div className="flex shrink-0 items-center gap-2">
            {project.caseStudy && (
              <Link
                to={project.caseStudy}
                onClick={(e) => e.stopPropagation()}
                className="flex items-center gap-2 px-4 py-2.5 bg-nothing-red text-white text-[11px] font-bold uppercase rounded-lg hover:bg-ink transition-colors"
              >
                {project.caseStudyLabel || "Case Study"} <ArrowUpRight size={15} />
              </Link>
            )}
            {isLink && (
              <span className="flex shrink-0 items-center gap-2 px-4 py-2.5 bg-ink text-white text-[11px] font-bold uppercase rounded-lg group-hover:bg-ink transition-colors">
                Visit <ArrowUpRight size={15} className="transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
              </span>
            )}
          </div>
        </div>
      </div>
    </TiltCard>
  );
};